'use client';

import React, { useEffect, useState } from 'react';
import { ProductList } from '@/ui/components/pages/products/components/product-list/product-list';
import { Filters } from '@/ui/components/pages/products/components/filters/filters';
import USPSection from '@/ui/components/pages/products/components/USPSection';
import { Product } from '@/ui/components/pages/products/types/Product';
import { extractProducts } from '@/ui/components/pages/products/utils/extractProducts';
import { extractTitle } from '@/ui/components/pages/products/utils/extractTitle';
import { extractUSP } from '@/ui/components/pages/products/utils/extractUSP';
import { extractCategories } from '@/ui/components/pages/products/utils/extractCategories';
import { getUniqueProductBadges } from '@/ui/components/product-badge/product-badge-utils';
import { CategoryOption } from '@/ui/components/pages/products/types/CategoryOption';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { LegalGuaranteeBadge } from '@/ui/components/legal-guarantee-badge/legal-guarantee-badge';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';

export interface ProductsProps {
  filter: string;
  data: any;
  priceVariantId: any;
}

export function Products({ filter, data, priceVariantId }: Readonly<ProductsProps>) {
  const productRuleService = useProductRuleService();
  const [products, setProducts] = useState<Product[]>([]);
  const [currency, setCurrency] = useState<string>('');
  const [title, setTitle] = useState<any>(null);
  const [uspData, setUspData] = useState<any[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  useEffect(() => {
    const components = data?.components ?? [];
    if (components && components.length > 0) {
      const getExtractedProducts = extractProducts(components, priceVariantId, productRuleService);
      setProducts(getExtractedProducts.products);
      setCurrency(getExtractedProducts.currency);
      setTitle(extractTitle(components));
      setUspData(extractUSP(components));
      setCategories(extractCategories(components));
    }
  }, [data, productRuleService]);

  const badgeFilters = getUniqueProductBadges(products.flatMap((product) => product.badges));

  return (
    <div className="mx-auto my-10 max-w-screen-2xl px-3 sm:my-14 sm:px-8 lg:my-20">
      <div className="space-y-6 md:space-y-10 lg:space-y-10">
        <RichTextRenderer
          blocks={title}
          className="text-[28px] leading-8 sm:text-4xl lg:text-5xl"
        />
        <USPSection uspData={uspData} />
        <LegalGuaranteeBadge withText />
        {products && (
          <>
            <Filters activeFilter={filter} categories={categories} badges={badgeFilters} />
            <ProductList products={products} currency={currency} filter={filter} />
          </>
        )}
      </div>
    </div>
  );
}
